import { TradeResponseInterface } from '@/components/organisms/Trade/interfaces/TradeInterfaces';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces/TokenInterface';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import fetchTradeableTokens, {
  getTradeEstimate,
  trade,
} from '@/services/TradeableTokensService';
import { generateTokenId } from '@/utils/functionalUtils';
import { logDebug, logError, logInfo, logWarning } from '@/utils/logger';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAuthorization } from './AuthorizationProvider';
import { useMarketData } from './MarketDataProvider';
type TokenWithBlockchain = {
  address: string;
  blockchain: string;
};
type TradeFeeResponse = {
  convenienceFee: string;
  convenienceFeeInUsd: string;
  gasCost: string;
  gasCostInUsd: string;
  bridgeFee: string;
  bridgeFeeInUsd: string;
  totalFee: string;
  totalFeeInUsd: string;
  amountOut: string;
  amountOutMin: string;
} | null;
type TradeableToken = TokenInterface | WalletTokenInterface;
interface TradeableTokensContextType {
  tradeableTokens: Array<TradeableToken>;
  loading: boolean;
  initialLoading: boolean;
  isForceFetchInProgress: boolean;
  error: Error | null;
  refreshTradeableTokens: () => Promise<any>;
  getTokens: (
    searchTerm?: string,
    limit?: number,
    blockchain?: string,
  ) => Array<TradeableToken>;
  getFeaturedTokens: (limit?: number, blockchain?: string) => Array<TradeableToken>;
  getTradeFee: (
    inputToken: TokenWithBlockchain,
    outputToken: TokenWithBlockchain,
    amount: string,
    yieldBearingNativeAssetId?: string,
  ) => Promise<TradeFeeResponse>;
  trade: (
    inputToken: TokenWithBlockchain,
    outputToken: TokenWithBlockchain,
    amount: string,
    yieldBearingNativeAssetId?: string,
  ) => Promise<TradeResponseInterface>;
  assetToBuy: TokenInterface | null;
  setAssetToBuy: (asset: TokenInterface | null) => void;
  isUserAuthorized: boolean;
}
let tradeableTokensProviderInstance: any = null;
export const setTradeableTokensForceFetchInProgress = (
  state: boolean,
): void => {
  if (tradeableTokensProviderInstance) {
    tradeableTokensProviderInstance._setForceFetchInProgress(state);
  }
};
const DEFAULT_CONTEXT: TradeableTokensContextType = {
  tradeableTokens: [],
  loading: false,
  initialLoading: true,
  isForceFetchInProgress: false,
  error: null,
  refreshTradeableTokens: async () => {},
  getTokens: () => [],
  getFeaturedTokens: () => [],
  getTradeFee: async () => null,
  trade: async () => ({
    txHash: '',
    status: 'failed',
    amount: '0',
    amountInUsd: '0',
    gasCost: '0',
    gasCostInUsd: '0',
    bridgeFee: '0',
    bridgeFeeInUsd: '0',
    sliverFee: '0',
    sliverFeeInUsd: '0',
  }),
  assetToBuy: null,
  setAssetToBuy: () => {},
  isUserAuthorized: true,
};
const TradeableTokensContext =
  createContext<TradeableTokensContextType>(DEFAULT_CONTEXT);
export const useTradeableTokens = () => useContext(TradeableTokensContext);
interface TradeableTokensProviderProps {
  children: ReactNode;
  staleTime?: number;
  retry?: number;
}
export const TradeableTokensProvider = ({
  children,
  staleTime = 3600000,
  retry = 5,
}: TradeableTokensProviderProps): JSX.Element => {
  const [isForceFetchInProgress, setIsForceFetchInProgress] = useState(false);
  const _setForceFetchInProgress = useCallback((state: boolean) => {
    setIsForceFetchInProgress(state);
  }, []);
  useEffect(() => {
    tradeableTokensProviderInstance = { _setForceFetchInProgress };
    return () => {
      tradeableTokensProviderInstance = null;
    };
  }, [_setForceFetchInProgress]);
  const queryClient = useQueryClient();
  const { isUserAuthorized } = useAuthorization();
  const { findTokenInWallet } = useWalletsContext();
  const { topErc20Tokens } = useMarketData();
  const marketDataMap = useMemo(() => {
    const startTime = performance.now();
    if (!topErc20Tokens?.length) {
      return new Map<string, TokenInterface>();
    }
    const map = new Map<string, TokenInterface>();
    const tokensCount = topErc20Tokens.length;
    logInfo(
      `Building market data map from ${tokensCount} top ERC20 tokens`,
      'TradeableTokensProvider.marketDataMap',
    );
    if (topErc20Tokens[0]) {
      logDebug(
        `Sample topErc20Token data: ${JSON.stringify({
          id: topErc20Tokens[0].id,
          address: topErc20Tokens[0].address,
          priceInUsd: topErc20Tokens[0].priceInUsd,
          marketCapInUsd: topErc20Tokens[0].marketCapInUsd,
        })}`,
        'TradeableTokensProvider.marketDataMap',
      );
    }
    let idCount = 0;
    let addressCount = 0;
    let missingDataCount = 0;
    try {
      for (let i = 0; i < tokensCount; i++) {
        const token = topErc20Tokens[i];
        try {
          if (!token.blockchain || !token.address) {
            continue;
          }
          const tokenId = token.id || generateTokenId(token).id;
          if (!token.priceInUsd && !token.marketCapInUsd) {
            missingDataCount++;
            logDebug(
              `Token ${token.symbol || token.address} missing market data`,
              'TradeableTokensProvider.marketDataMap',
            );
          }
          if (tokenId) {
            map.set(tokenId, token);
            idCount++;
            const lowerAddress = token.address.toLowerCase();
            map.set(lowerAddress, token);
            addressCount++;
          }
        } catch (err) {
          logError(err as Error, 'TradeableTokensProvider.marketDataMap', {
            token: {
              address: token.address,
              blockchain: token.blockchain,
              symbol: token.symbol,
            },
          });
        }
      }
      logDebug(
        `Market data map built with ${idCount} tokens by ID and ${addressCount} by address. ${missingDataCount} tokens missing market data.`,
        'TradeableTokensProvider.marketDataMap',
      );
    } catch (err) {
      logError(err as Error, 'TradeableTokensProvider.marketDataMap');
    }
    const endTime = performance.now();
    logDebug(
      `Market data map creation took ${(endTime - startTime).toFixed(2)}ms`,
      'TradeableTokensProvider.marketDataMap',
    );
    return map;
  }, [topErc20Tokens]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [assetToBuy, setAssetToBuy] = useState<TokenInterface | null>(null);
  const {
    data = [],
    error,
    isLoading,
    refetch: refetchTradeableTokensQuery,
  } = useQuery<Array<TradeableToken>, Error>({
    queryKey: ['tradeableTokens'],
    queryFn: async () => {
      const safeFetchTradeableTokens = async (network: string) => {
        try {
          return await fetchTradeableTokens(network);
        } catch (error) {
          logError(
            error,
            'TradeableTokensProvider.safeFetchTradeableTokens.error',
            { network },
          );
          return [];
        }
      };
      const promises = APP_CONFIG.TRADEABLE_TOKENS_SUPPORTED_NETWORKS.map(
        (network) => safeFetchTradeableTokens(network),
      );
      const results = await Promise.all(promises);
      return results.flat();
    },
    staleTime,
    retry,
    placeholderData: (previousData) => previousData,
  });
  useEffect(() => {
    if (!isLoading) {
      setInitialLoading(false);
      logDebug(
        `Tradeable tokens loaded: ${data.length} tokens available`,
        'TradeableTokensProvider.loadingComplete',
      );
    }
  }, [isLoading, data.length]);
  const enhancedData = useMemo(() => {
    if (!data.length) {
      if (topErc20Tokens?.length) {
        logWarning(
          'No tradeable tokens from API, falling back to topErc20Tokens',
          'TradeableTokensProvider.enhancedData',
        );
        return topErc20Tokens;
      }
      return [];
    }
    const startTime = performance.now();
    let idGeneratedCount = 0;
    let walletMergeCount = 0;
    const dataLength = data.length;
    const enhancedTokens = new Array(dataLength);
    for (let i = 0; i < dataLength; i++) {
      const token = data[i];
      const tokenInWallet = findTokenInWallet(token);
      if (!tokenInWallet) {
        if (!token.id && token.blockchain && token.address) {
          idGeneratedCount++;
          enhancedTokens[i] = generateTokenId(token);
        } else {
          enhancedTokens[i] = token;
        }
        continue;
      }
      walletMergeCount++;
      const mergedToken = { ...token, ...tokenInWallet };
      if (!mergedToken.id && mergedToken.blockchain && mergedToken.address) {
        idGeneratedCount++;
        enhancedTokens[i] = generateTokenId(mergedToken);
      } else {
        enhancedTokens[i] = mergedToken;
      }
    }
    const endTime = performance.now();
    const timeMs = (endTime - startTime).toFixed(2);
    logDebug(
      `Enhanced ${dataLength} tokens: ${walletMergeCount} merged with wallet data, ${idGeneratedCount} IDs generated. Took ${timeMs}ms`,
      'TradeableTokensProvider.enhancedData',
    );
    return enhancedTokens;
  }, [data, findTokenInWallet]);
  const getTokens = useCallback(
    (searchTerm = '', limit = 0, blockchain?: string) => {
      const totalStartTime = performance.now();
      if (!enhancedData.length) {
        return [];
      }
      let filteredTokens = enhancedData;
      if (blockchain) {
        const normalizedBlockchain = blockchain.toLowerCase();
        filteredTokens = filteredTokens.filter(
          (token) => token.blockchain?.toLowerCase() === normalizedBlockchain,
        );
      }
      if (searchTerm) {
        const searchStartTime = performance.now();
        const normalizedSearchTerm = searchTerm.toLowerCase();
        filteredTokens = filteredTokens.filter((token) => {
          if (
            token.name &&
            token.name.toLowerCase().includes(normalizedSearchTerm)
          ) {
            return true;
          }
          if (
            token.symbol &&
            token.symbol.toLowerCase().includes(normalizedSearchTerm)
          ) {
            return true;
          }
          return (
            token.address &&
            token.address.toLowerCase().includes(normalizedSearchTerm)
          );
        });
        const searchEndTime = performance.now();
        logDebug(
          `Search filtered to ${filteredTokens.length} tokens in ${(
            searchEndTime - searchStartTime
          ).toFixed(2)}ms`,
          'TradeableTokensProvider.getTokens.search',
        );
      }
      try {
        if (marketDataMap.size > 0 && filteredTokens.length > 0) {
          const enrichStartTime = performance.now();
          let enrichedCount = 0;
          let alreadyHadDataCount = 0;
          let idMatchCount = 0;
          let addressMatchCount = 0;
          let generatedIdMatchCount = 0;
          let noMatchCount = 0;
          const tokenCount = filteredTokens.length;
          const resultTokens = new Array(tokenCount);
          let hasLoggedMatchingExample = false;
          for (let i = 0; i < tokenCount; i++) {
            const token = filteredTokens[i];
            if (
              (token.priceInUsd && token.priceInUsd > 0) ||
              (token.marketCapInUsd && token.marketCapInUsd > 0)
            ) {
              alreadyHadDataCount++;
              resultTokens[i] = token;
              continue;
            }
            let marketData = null;
            let matchMethod = '';
            if (token.id) {
              marketData = marketDataMap.get(token.id);
              if (marketData) {
                idMatchCount++;
                matchMethod = 'id';
              }
            }
            if (!marketData && token.address) {
              const lowerAddress = token.address.toLowerCase();
              marketData = marketDataMap.get(lowerAddress);
              if (marketData) {
                addressMatchCount++;
                matchMethod = 'address';
              }
            }
            if (!marketData && token.blockchain && token.address) {
              try {
                const generatedId = generateTokenId(token).id;
                marketData = marketDataMap.get(generatedId);
                if (marketData) {
                  generatedIdMatchCount++;
                  matchMethod = 'generated-id';
                }
              } catch (err) {}
            }
            if (marketData) {
              enrichedCount++;
              resultTokens[i] = {
                ...token,
                priceInUsd: token.priceInUsd || marketData.priceInUsd,
                marketCapInUsd:
                  token.marketCapInUsd || marketData.marketCapInUsd,
              };
            } else {
              noMatchCount++;
              resultTokens[i] = token;
            }
          }
          filteredTokens = resultTokens;
          const enrichEndTime = performance.now();
          logInfo(
            `Market data enrichment: ${enrichedCount} enriched (${idMatchCount} by ID, ${addressMatchCount} by address, ${generatedIdMatchCount} by generated ID), ${alreadyHadDataCount} already had data, ${noMatchCount} no match. Took ${(
              enrichEndTime - enrichStartTime
            ).toFixed(2)}ms`,
            'TradeableTokensProvider.getTokens.enrichment',
          );
        }
      } catch (err) {
        logError(err as Error, 'TradeableTokensProvider.getTokens.enrichment', {
          marketDataMapSize: marketDataMap.size,
          filteredTokensCount: filteredTokens.length,
        });
      }
      const finalResult =
        limit > 0 ? filteredTokens.slice(0, limit) : filteredTokens;
      const totalEndTime = performance.now();
      logDebug(
        `Token search for "${searchTerm}" on ${
          blockchain || 'all-chains'
        } returned ${finalResult.length} results in ${(
          totalEndTime - totalStartTime
        ).toFixed(2)}ms`,
        'TradeableTokensProvider.getTokens',
      );
      return finalResult;
    },
    [enhancedData, marketDataMap, topErc20Tokens],
  );
  const getFeaturedTokens = useCallback(
    (limit?: number, blockchain?: string) => {
      const startTime = performance.now();
      if (!enhancedData.length) {
        return [];
      }
      const filteredTokens = blockchain
        ? enhancedData.filter(
            (token) => token.blockchain?.toLowerCase() === blockchain.toLowerCase(),
          )
        : enhancedData;
      const sortedTokens = [...filteredTokens].sort((a, b) => {
        const marketCapA = a.marketCapInUsd || 0;
        const marketCapB = b.marketCapInUsd || 0;
        return marketCapB - marketCapA;
      });
      const effectiveLimit =
        limit || APP_CONFIG.BUY_TOKEN_DROPDOWN_FEATURED_TOKENS_COUNT;
      const result = sortedTokens.slice(0, effectiveLimit);
      const endTime = performance.now();
      const processingTime = (endTime - startTime).toFixed(2);
      logDebug(
        `getFeaturedTokens returned ${result.length} tokens for ${
          blockchain || 'all-chains'
        } in ${processingTime}ms`,
        'TradeableTokensProvider.getFeaturedTokens',
      );
      return result;
    },
    [enhancedData],
  );
  const refreshTradeableTokens = useCallback(async () => {
    const caller = 'TradeableTokensProvider.refreshTradeableTokens';
    logInfo('Starting manual refresh of tradeable tokens.', caller);
    logInfo('Invalidating tradeable tokens query.', caller);
    queryClient.invalidateQueries({ queryKey: ['tradeableTokens'] });
    logInfo('Refetching tradeable tokens.', caller);
    return await refetchTradeableTokensQuery();
  }, [queryClient, refetchTradeableTokensQuery]);
  const getTradeFee = useCallback(
    async (
      inputToken: TokenWithBlockchain,
      outputToken: TokenWithBlockchain,
      amount: string,
      yieldBearingNativeAssetId?: string,
    ): Promise<TradeFeeResponse> => {
      logDebug(
        `Estimating trade fee for ${amount} from ${inputToken.address} to ${outputToken.address}`,
        'TradeableTokensProvider.getTradeFee',
      );
      try {
        const response = await getTradeEstimate(
          inputToken,
          outputToken,
          amount,
          yieldBearingNativeAssetId,
        );
        const result = {
          convenienceFee: response.sliverFee,
          convenienceFeeInUsd: response.sliverFeeInUsd,
          gasCost: response.gasCost,
          gasCostInUsd: response.gasCostInUsd,
          bridgeFee: response.bridgeFee || '0',
          bridgeFeeInUsd: response.bridgeFeeInUsd || '0',
          totalFee: response.totalFee,
          totalFeeInUsd: response.totalFeeInUsd,
          amountOut: response.amountOut,
          amountOutMin: response.amountOutMin,
        };
        logDebug(
          `Trade fee estimate successful: ${result.totalFeeInUsd} USD total fee`,
          'TradeableTokensProvider.getTradeFee',
        );
        return result;
      } catch (error) {
        logError(error, 'TradeableTokensProvider.getTradeFee', {
          inputToken,
          outputToken,
          amount,
        });
        throw error;
      }
    },
    [],
  );
  const handleTrade = useCallback(
    async (
      inputToken: TokenWithBlockchain,
      outputToken: TokenWithBlockchain,
      amount: string,
      yieldBearingNativeAssetId?: string,
    ): Promise<TradeResponseInterface> => {
      logInfo(
        `Executing trade of ${amount} from ${inputToken.address} to ${outputToken.address}`,
        'TradeableTokensProvider.handleTrade',
      );
      try {
        const response = await trade(
          inputToken,
          outputToken,
          amount,
          yieldBearingNativeAssetId,
        );
        if (response.txHash) {
          logInfo(
            `Trade successful with transaction hash: ${response.txHash}`,
            'TradeableTokensProvider.handleTrade',
          );
          queryClient.invalidateQueries({ queryKey: ['wallets'] });
        } else {
          logError(
            'Trade completed but no transaction hash returned',
            'TradeableTokensProvider.handleTrade',
            { response },
          );
        }
        return response;
      } catch (error) {
        logError(error, 'TradeableTokensProvider.handleTrade', {
          inputToken,
          outputToken,
          amount,
        });
        throw error;
      }
    },
    [queryClient],
  );
  useEffect(() => {
    if (error) {
      const context = data.length ? 'Refresh Error' : 'Initial Load Error';
      logError(error, 'TradeableTokensProvider.dataFetchError', {
        context,
        dataLength: data.length,
      });
    }
  }, [error, data.length]);
  useEffect(() => {
    if (isForceFetchInProgress) {
      logInfo(
        'Force fetch in progress for tradeable tokens',
        'TradeableTokensProvider',
      );
    }
  }, [isForceFetchInProgress]);
  const effectiveInitialLoading = initialLoading || isForceFetchInProgress;
  const contextValue = useMemo(
    () => ({
      tradeableTokens:
        isForceFetchInProgress && enhancedData.length === 0 ? [] : enhancedData,
      loading: isLoading || isForceFetchInProgress,
      initialLoading: effectiveInitialLoading,
      isForceFetchInProgress,
      error: isForceFetchInProgress ? null : error ?? null,
      refreshTradeableTokens,
      getTokens,
      getFeaturedTokens,
      getTradeFee,
      trade: handleTrade,
      assetToBuy,
      setAssetToBuy,
      isUserAuthorized,
    }),
    [
      enhancedData,
      isLoading,
      isForceFetchInProgress,
      initialLoading,
      error,
      refreshTradeableTokens,
      getTokens,
      getFeaturedTokens,
      getTradeFee,
      handleTrade,
      assetToBuy,
      setAssetToBuy,
      isUserAuthorized,
    ],
  );
  return (
    <TradeableTokensContext.Provider value={contextValue}>
      {children}
    </TradeableTokensContext.Provider>
  );
};
